import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import {
  DonationTracking,
  DonationTrackingRoutingLog,
  DonationTrackingService,
  DonationTrackingStatus,
} from '../../services/DonationTrackingService';

const STATUS_ORDER: DonationTrackingStatus[] = [
  'RECEIVED',
  'PROCESSING',
  'DISPATCHING',
  'DELIVERED',
];

const STATUS_LABELS: Record<DonationTrackingStatus, string> = {
  RECEIVED: 'Received',
  PROCESSING: 'Processing',
  DISPATCHING: 'Dispatching',
  DELIVERED: 'Delivered',
};

const DonationTrackingDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tracking, setTracking] = useState<DonationTracking | null>(null);
  const [routingLogs, setRoutingLogs] = useState<DonationTrackingRoutingLog[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [statusDraft, setStatusDraft] = useState<DonationTrackingStatus | ''>('');
  const [statusMessage, setStatusMessage] = useState('');
  const [etaDraft, setEtaDraft] = useState('');
  const [labelDraft, setLabelDraft] = useState('');
  const [regionDraft, setRegionDraft] = useState('');
  const [countDraft, setCountDraft] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [data, logs] = await Promise.all([
        DonationTrackingService.getById(id),
        DonationTrackingService.getRoutingLog(id),
      ]);
      setTracking(data);
      setRoutingLogs(logs);
      setStatusDraft(data.status);
      setEtaDraft(data.estimatedDeliveryDate?.slice(0, 10) ?? '');
      setLabelDraft(data.beneficiaryLabel);
      setRegionDraft(data.beneficiaryRegion);
      setCountDraft(String(data.beneficiaryCount));
    } catch {
      setError('Failed to load tracking details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const activeStep = useMemo(() => {
    if (!tracking) return 0;
    return STATUS_ORDER.indexOf(tracking.status);
  }, [tracking]);

  const handleStatusUpdate = async () => {
    if (!tracking || !statusDraft) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await DonationTrackingService.updateStatus(
        tracking.id,
        statusDraft,
        statusMessage.trim() || undefined,
      );
      setTracking(updated);
      setStatusMessage('');
      setNotice(`Status updated to ${updated.status}.`);
    } catch {
      setError('Failed to update status.');
    } finally {
      setSaving(false);
    }
  };

  const handleDetailsUpdate = async () => {
    if (!tracking) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload: Parameters<typeof DonationTrackingService.updateDetails>[1] = {};
      if (etaDraft) {
        payload.estimatedDeliveryDate = new Date(etaDraft).toISOString();
      }
      if (labelDraft) payload.beneficiaryLabel = labelDraft;
      if (regionDraft) payload.beneficiaryRegion = regionDraft;
      const parsedCount = Number(countDraft);
      if (Number.isFinite(parsedCount) && parsedCount >= 0) {
        payload.beneficiaryCount = Math.floor(parsedCount);
      }
      const updated = await DonationTrackingService.updateDetails(
        tracking.id,
        payload,
      );
      setTracking(updated);
      setNotice('Tracking details saved.');
    } catch {
      setError('Failed to update details.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !tracking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Button component={RouterLink} to="/donation-tracking" size="small">
          ← Back
        </Button>
        <Typography variant="h5">Tracking Details</Typography>
        <Chip size="small" label={tracking.status} color="primary" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Donation {tracking.donationId} · Created{' '}
        {new Date(tracking.createdAt).toLocaleString()}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STATUS_ORDER.map((status) => (
            <Step key={status}>
              <StepLabel>{STATUS_LABELS[status]}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Donation
            </Typography>
            <Stack spacing={1}>
              {tracking.donation && (
                <>
                  <Typography variant="body2">
                    Amount: {tracking.donation.amount} {tracking.donation.currency}
                  </Typography>
                  <Typography variant="body2">
                    Status: {tracking.donation.status}
                  </Typography>
                  <Typography variant="body2">
                    Created: {new Date(tracking.donation.createdAt).toLocaleString()}
                  </Typography>
                </>
              )}
              <Typography variant="body2">
                Cause: {tracking.cause?.title ?? '—'}
              </Typography>
              <Typography variant="body2">User: {tracking.userId}</Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Beneficiary & Routing
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                Label: {tracking.beneficiaryLabel}
              </Typography>
              <Typography variant="body2">
                Country: {tracking.beneficiaryCountry}
              </Typography>
              <Typography variant="body2">
                Region: {tracking.beneficiaryRegion}
              </Typography>
              <Typography variant="body2">
                Approx. coords: {tracking.latitude.toFixed(4)}, {tracking.longitude.toFixed(4)}
              </Typography>
              <Typography variant="body2">
                Beneficiary count: {tracking.beneficiaryCount}
              </Typography>
              <Typography variant="body2">
                Estimated delivery:{' '}
                {tracking.estimatedDeliveryDate
                  ? new Date(tracking.estimatedDeliveryDate).toLocaleDateString()
                  : '—'}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Update status
            </Typography>
            <Stack spacing={2}>
              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusDraft}
                  onChange={(e: SelectChangeEvent) =>
                    setStatusDraft(e.target.value as DonationTrackingStatus)
                  }
                >
                  {STATUS_ORDER.map((status) => (
                    <MenuItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Message (optional)"
                value={statusMessage}
                onChange={(e) => setStatusMessage(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleStatusUpdate}
                disabled={saving || !statusDraft}
              >
                Update status
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Edit details
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Beneficiary label"
                value={labelDraft}
                onChange={(e) => setLabelDraft(e.target.value)}
              />
              <TextField
                size="small"
                label="Region"
                value={regionDraft}
                onChange={(e) => setRegionDraft(e.target.value)}
              />
              <TextField
                size="small"
                label="Beneficiary count"
                type="number"
                value={countDraft}
                onChange={(e) => setCountDraft(e.target.value)}
              />
              <TextField
                size="small"
                label="Estimated delivery"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={etaDraft}
                onChange={(e) => setEtaDraft(e.target.value)}
              />
              <Button
                variant="outlined"
                onClick={handleDetailsUpdate}
                disabled={saving}
              >
                Save details
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Timeline
            </Typography>
            <Stack spacing={2} divider={<Divider flexItem />}>
              {(tracking.events ?? []).map((event) => (
                <Stack
                  key={event.id}
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                >
                  <Chip size="small" label={event.status} color="primary" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{event.message}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(event.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Stack>
              ))}
              {(tracking.events ?? []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No timeline events yet.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Routing logs
            </Typography>
            <Stack spacing={2} divider={<Divider flexItem />}>
              {routingLogs.map((log) => (
                <Box key={log.id}>
                  <Typography variant="body2">
                    {log.assignedCountry} · {log.assignedRegion} ·{' '}
                    {log.beneficiaryCount} beneficiaries
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {log.routingReason} ·{' '}
                    {new Date(log.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              ))}
              {routingLogs.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No routing logs recorded.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DonationTrackingDetailsPage;
